import { z } from "zod";
import { apiIntIdSchema } from "./ids.js";
/**
 * Message credit count string schema.
 * Represents a non-negative 64-bit signed integer crossing HTTP boundaries.
 * Coerces integer numbers to strings for JSON ergonomics.
 */
export const messageCreditsSchema = z
    .union([z.string(), z.number().int().nonnegative()])
    .pipe(z.coerce.string())
    .pipe(z
    .string()
    .regex(/^(0|[1-9][0-9]*)$/)
    .refine((val) => {
    try {
        const n = BigInt(val);
        return n >= 0n && n <= 9223372036854775807n;
    }
    catch {
        return false;
    }
}, { message: "Credits must be a non-negative 64-bit integer" }));
export const superAdminOrganizationMessageBalanceResponseSchema = z.object({
    organizationId: apiIntIdSchema,
    isUnlimited: z.boolean(),
    availableCredits: messageCreditsSchema,
    reservedCredits: messageCreditsSchema,
    usedCredits: messageCreditsSchema,
    totalAddedCredits: messageCreditsSchema,
    totalDeductedCredits: messageCreditsSchema,
    usageTrackingStartedAt: z.string().datetime({ offset: true }),
    version: z.number().int().positive(),
});
export const organizationMessageBalanceResponseSchema = z.object({
    isUnlimited: z.boolean(),
    availableCredits: messageCreditsSchema,
    reservedCredits: messageCreditsSchema,
});
export const superAdminUpdateMessageBalanceOperationSchema = z.enum([
    "ADD",
    "DEDUCT",
    "SET_LIMITED",
    "SET_UNLIMITED",
]);
export const superAdminUpdateMessageBalanceRequestSchema = z
    .object({
    operation: superAdminUpdateMessageBalanceOperationSchema,
    amount: messageCreditsSchema.optional(),
    version: z.number().int().positive().optional(),
})
    .strict()
    .superRefine((data, ctx) => {
    if (data.operation === "ADD" || data.operation === "DEDUCT") {
        if (data.amount === undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["amount"],
                message: `${data.operation} requires an amount`,
            });
        }
        else if (BigInt(data.amount) <= 0n) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["amount"],
                message: `${data.operation} amount must be greater than 0`,
            });
        }
    }
    else if (data.operation === "SET_LIMITED") {
        if (data.amount === undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["amount"],
                message: "SET_LIMITED requires an amount",
            });
        }
    }
    else if (data.operation === "SET_UNLIMITED") {
        if (data.amount !== undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["amount"],
                message: "SET_UNLIMITED does not accept an amount",
            });
        }
    }
});
export const messageBalanceBlockedJobsResponseSchema = z.object({
    count: z.number().int().nonnegative(),
    fingerprint: z.string(),
});
export const messageBalanceResumeBlockedJobsRequestSchema = z
    .object({
    expectedCount: z.number().int().nonnegative(),
    fingerprint: z.string().trim().min(1).optional(),
})
    .superRefine((data, ctx) => {
    if (data.expectedCount > 0 && !data.fingerprint) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["fingerprint"],
            message: "fingerprint is required when expectedCount > 0",
        });
    }
});
export const messageBalanceDiscardBlockedJobsRequestSchema = z
    .object({
    expectedCount: z.number().int().nonnegative(),
    fingerprint: z.string().trim().min(1).optional(),
})
    .superRefine((data, ctx) => {
    if (data.expectedCount > 0 && !data.fingerprint) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["fingerprint"],
            message: "fingerprint is required when expectedCount > 0",
        });
    }
});
//# sourceMappingURL=message-balance-contracts.js.map